<script>
export default {
  name: 'BarrasProductos',
  props: {
    labels: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    },
    detalle: {
      type: Array,
      default: () => []
    },
    vista: {
      type: String,
      default: 'ventas'
    },
    top: {
      type: Number,
      default: 15
    }
  },
  data() {
    return {
      chartInstance: null
    };
  },
  computed: {
    chartData() {
      if (!this.detalle || this.detalle.length === 0) {
        return {
          labels: [],
          datasets: []
        };
      }

      // Tomar los top productos basado en ventas totales
      const productosConTotales = this.labels.map((label, index) => {
        const detalleProducto = this.detalle[index];
        const region = detalleProducto && detalleProducto.regionMuestra ? detalleProducto.regionMuestra : '';
        return {
          producto: label,
          productoCorto: this.acortarNombreProducto(label, region),
          totalVentas: this.data[index] || 0,
          originalIndex: index
        };
      }).sort((a, b) => b.totalVentas - a.totalVentas).slice(0, this.top);

      const productosOrdenados = productosConTotales.map(p => p.productoCorto);

      // Obtener todas las empresas únicas
      const empresasUnicas = new Set();
      this.detalle.forEach(productoItem => {
        if (productoItem.empresas && Array.isArray(productoItem.empresas)) {
          productoItem.empresas.forEach(empresa => {
            empresasUnicas.add(empresa.nombreEmpresa);
          });
        }
      });

      const listaEmpresas = Array.from(empresasUnicas);

      // Crear datasets por empresa
      const datasets = listaEmpresas.map(nombreEmpresa => {
        const data = productosConTotales.map(productoInfo => {
          // Usar el índice original para buscar en detalle
          const productoItem = this.detalle[productoInfo.originalIndex];

          if (productoItem && productoItem.empresas && Array.isArray(productoItem.empresas)) {
            const empresaData = productoItem.empresas.find(e => e.nombreEmpresa === nombreEmpresa);
            if (empresaData) {
              return this.vista === 'ventas' ? empresaData.totalVentas : empresaData.totalUnidades;
            }
          }
          return 0;
        });

        return {
          label: nombreEmpresa,
          data: data,
          backgroundColor: this.getColorForEmpresa(nombreEmpresa),
          borderColor: this.getColorForEmpresa(nombreEmpresa),
          borderWidth: 1
        };
      });

      // Guardar datos completos para tooltips
      this.datosCompletos = {
        productos: productosConTotales,
        detalle: this.detalle
      };

      return {
        labels: productosOrdenados,
        datasets: datasets
      };
    },

    chartOptions() {
      const self = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          xAxes: [{
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              fontSize: 10
            },
            gridLines: {
              display: false
            }
          }],
          yAxes: [{
            ticks: {
              beginAtZero: true,
              callback: function(value) {
                const numValue = parseFloat(value);
                
                if (self.vista === 'ventas') {
                  if (numValue >= 1000000000) {
                    return '$' + (numValue / 1000000000).toFixed(1) + 'B';
                  } else if (numValue >= 1000000) {
                    return '$' + (numValue / 1000000).toFixed(0) + 'M';
                  } else if (numValue >= 1000) {
                    return '$' + (numValue / 1000).toFixed(0) + 'K';
                  } else {
                    return '$' + Math.round(numValue);
                  }
                } else {
                  return self.formatNumber(numValue);
                }
              }
            },
            gridLines: {
              display: true,
              color: 'rgba(0,0,0,0.1)'
            }
          }]
        },
        legend: {
          display: true,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 15
          }
        },
        tooltips: {
          backgroundColor: 'rgba(0,0,0,0.9)',
          titleFontColor: 'white',
          bodyFontColor: 'white',
          borderColor: 'rgba(255,255,255,0.3)',
          borderWidth: 1,
          cornerRadius: 6,
          mode: 'index',
          intersect: false,
          callbacks: {
            title: function(tooltipItems, data) {
              return tooltipItems[0].label;
            },
            label: function(tooltipItem, data) {
              const empresa = data.datasets[tooltipItem.datasetIndex].label;
              const productoIndex = tooltipItem.index;
              
              if (self.datosCompletos && self.datosCompletos.productos) {
                const productoInfo = self.datosCompletos.productos[productoIndex];
                const productoItem = self.datosCompletos.detalle[productoInfo.originalIndex];

                if (productoItem && productoItem.empresas && Array.isArray(productoItem.empresas)) {
                  const empresaData = productoItem.empresas.find(e => e.nombreEmpresa === empresa);
                  if (empresaData) {
                    if (self.vista === 'ventas') {
                      return [
                        `${empresa}: ${self.formatPrice(empresaData.totalVentas)}`,
                        `Código: ${productoItem.codigoProducto}`,
                        `Formato: ${self.extraerFormato(productoItem.nombreProducto)}kg`,
                        `Unidades: ${self.formatNumber(empresaData.totalUnidades)}`,
                        `Región: ${productoItem.regionMuestra}`
                      ];
                    } else {
                      return [
                        `${empresa}: ${self.formatNumber(empresaData.totalUnidades)} unidades`,
                        `Código: ${productoItem.codigoProducto}`,
                        `Formato: ${self.extraerFormato(productoItem.nombreProducto)}kg`,
                        `Ventas: ${self.formatPrice(empresaData.totalVentas)}`,
                        `Región: ${productoItem.regionMuestra}`
                      ];
                    }
                  }
                }
              }
              
              const valor = tooltipItem.value;
              return `${empresa}: ${self.vista === 'ventas' ? self.formatPrice(valor) : self.formatNumber(valor)}`;
            }
          }
        }
      };
    }
  },

  methods: {
    extraerFormato(nombreProducto) {
      const match = nombreProducto.match(/(\d+)\s*K/i);
      return match ? match[1] : 'Unknown';
    },

    acortarNombreProducto(nombreCompleto, region = '') {
      const codigoMatch = nombreCompleto.match(/\((\d+)\)/);
      const formatoMatch = nombreCompleto.match(/(\d+)\s*K/i);
      
      const codigo = codigoMatch ? codigoMatch[1] : '';
      const formato = formatoMatch ? formatoMatch[1] + 'kg' : '';
      
      // Si se proporciona región, incluirla en el label
      if (region) {
        return `${codigo} - ${formato} (${region})`;
      }
      
      return `${codigo} - ${formato}`;
    },

    getColorForEmpresa(empresa) {
      const colores = {
        'GASCO GLP S A': '#0D47A1',
        'GASCO GLP S.A.': '#0D47A1', 
        'ABASTIBLE S.A.': '#FF9800',
        'EMPRESAS LIPIGAS S A': '#ffc107',
        'EMPRESAS LIPIGAS S.A.': '#ffc107'
      };
      return colores[empresa] || '#9E9E9E';
    },

    getColorByFormato(formato) {
      const colores = {
        '5': '#4CAF50',
        '11': '#2196F3',
        '15': '#FF9800',
        '45': '#9C27B0'
      };
      return colores[formato] || '#9E9E9E';
    },

    formatPrice(value) {
      if (!value) return '$0';
      const numValue = parseFloat(value);
      
      if (numValue >= 1000000000) {
        return '$' + (numValue / 1000000000).toFixed(1) + 'B';
      } else if (numValue >= 1000000) {
        return '$' + (numValue / 1000000).toFixed(0) + 'M';
      } else if (numValue >= 1000) {
        return '$' + (numValue / 1000).toFixed(0) + 'K';
      } else {
        return '$' + Math.round(numValue).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      }
    },

    formatNumber(value) {
      if (!value) return '0';
      const numValue = Math.round(parseFloat(value));
      return numValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },

    renderChart() {
      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      const ctx = this.$refs.canvas.getContext('2d');
      const Chart = window.Chart;

      if (!Chart) {
        console.error('Chart.js no está disponible');
        return;
      }

      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: this.chartData,
        options: this.chartOptions
      });
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.renderChart();
    });
  },

  watch: {
    chartData: {
      handler() {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
      deep: true
    },
    vista() {
      this.$nextTick(() => {
        this.renderChart();
      });
    }
  },

  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  }
};
</script>

<template>
  <div style="position: relative; height: 100%;">
    <div v-if="!detalle || detalle.length === 0" class="text-xs-center pa-4">
      <v-icon size="48" color="grey lighten-2">whatshot</v-icon>
      <div class="subheading grey--text mt-2">Sin datos de productos</div>
    </div>
    <canvas v-else ref="canvas"></canvas>
    
    <div v-if="detalle && detalle.length > 0" class="mt-2">
      <v-layout row wrap justify-center>
        <v-chip small class="ma-1" :color="getColorForEmpresa('GASCO GLP S A')" text-color="white">GASCO</v-chip>
        <v-chip small class="ma-1" :color="getColorForEmpresa('ABASTIBLE S.A.')" text-color="white">ABASTIBLE</v-chip>
        <v-chip small class="ma-1" :color="getColorForEmpresa('EMPRESAS LIPIGAS S A')" text-color="white">LIPIGAS</v-chip>
      </v-layout>
    </div>
  </div>
</template>